import type { IconName } from '@sparklayer-io/spark-dash-ui/components/ui/icon/types';
import { Accordion, Badge } from '@ui/themed';
import { forwardRef, useCallback, useEffect, useMemo } from 'react';
import { ChecklistItemContext } from '../context/item-context';
import { useChecklistRoot } from '../context/root-context';
import { useStepStatus } from '../engine/use-step-status';
import type {
  ChecklistItemDisplayState,
  ChecklistItemProps,
  EffectiveChecklistItemStatus,
} from '../types';

type ChecklistItemView = {
  displayState: ChecklistItemDisplayState | undefined;
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
};

// The item stays mounted while collapsed (only the accordion body unmounts), so `status` keeps updating.
function useChecklistItem(
  id: string,
  status: EffectiveChecklistItemStatus | undefined,
): ChecklistItemView {
  const root = useChecklistRoot();
  const { syncItem, removeItem, setExpanded } = root;

  useEffect(() => {
    syncItem(id, status);
  }, [id, status, syncItem]);

  // Unmount-only (keyed on `id`) so a status change doesn't drop the item's order slot.
  useEffect(() => {
    return () => removeItem(id);
  }, [id, removeItem]);

  return {
    displayState: root.getDisplayState(id),
    isExpanded: root.expandedId === id,
    onExpandedChange: (expanded) => setExpanded(id, expanded),
  };
}

export const ChecklistItem = forwardRef<HTMLDivElement, ChecklistItemProps>(
  function ChecklistItem(props, ref) {
    const { storageKeyBuilder, setNode } = useChecklistRoot();
    const { status, isSkipped, skip, unskip } = useStepStatus(
      props.id,
      props.useStatus,
      storageKeyBuilder,
    );
    const { displayState, isExpanded, onExpandedChange } = useChecklistItem(props.id, status);

    const itemCtx = useMemo(
      () => ({ status, isExpanded, isSkipped, skip, unskip }),
      [status, isExpanded, isSkipped, skip, unskip],
    );

    // Register the node for completion-edge anchoring (see `onItemComplete`), still honouring a
    // forwarded ref.
    const id = props.id;
    const registerNode = useCallback(
      (el: HTMLDivElement | null) => {
        setNode(id, el);
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      },
      [setNode, id, ref],
    );

    // The badge follows `status`, not the gate: a step done out of order is shown locked (padlock)
    // but still carries its badge. Completed → tick; skipped → clock.
    const badgeIcon: IconName | null =
      status === 'complete' ? 'check' : status === 'skipped' ? 'clock' : null;
    const title = badgeIcon ? (
      <span className="inline-flex items-center gap-2">
        {props.title}
        <Badge variant="default" size="sm" icon={badgeIcon} />
      </span>
    ) : (
      props.title
    );

    return (
      <ChecklistItemContext.Provider value={itemCtx}>
        <Accordion.Item
          ref={registerNode}
          title={title}
          icon={props.icon}
          disabled={displayState === 'locked' ? 'locked' : undefined}
          isExpanded={isExpanded}
          onExpandedChange={onExpandedChange}
        >
          {props.children}
        </Accordion.Item>
      </ChecklistItemContext.Provider>
    );
  },
);
ChecklistItem.displayName = 'Checklist.Item';
